import { z } from 'zod';

/**
 * The life of one entitlement.
 *
 * `active → grace → expired` is the ordinary decay, and `revoked` is terminal:
 * a refund or a staff reversal, never something a renewal walks back. The
 * distinction matters because `grace` **still entitles** — a card that failed
 * its retry is a billing problem, not a reason to lock a member out mid-cycle —
 * while `expired` and `revoked` do not.
 *
 * A ban is **not** here. A banned member keeps the entitlement they paid for
 * (`03-payments-iap.md`: a ban never entitles anyone to a refund, and it does
 * not confiscate one either); they simply cannot sign in.
 */
export const ENTITLEMENT_STATUSES = ['active', 'grace', 'expired', 'revoked'] as const;

export const EntitlementStatusSchema = z.enum(ENTITLEMENT_STATUSES);

export type EntitlementStatus = (typeof ENTITLEMENT_STATUSES)[number];

const STATUS_SET: ReadonlySet<string> = new Set(ENTITLEMENT_STATUSES);

export function isEntitlementStatus(value: string): value is EntitlementStatus {
  return STATUS_SET.has(value);
}

/**
 * The statuses that grant access — the one place "entitled" is defined, so a
 * query, a sweep and a guard cannot drift apart on whether `grace` counts.
 */
export const LIVE_ENTITLEMENT_STATUSES: readonly EntitlementStatus[] = ['active', 'grace'];

/** Whether a status grants access at all. Says nothing about the period end. */
export function isLiveEntitlementStatus(status: EntitlementStatus): boolean {
  return LIVE_ENTITLEMENT_STATUSES.includes(status);
}
